<template>
  <div class="banner-settings">
    <!-- 添加加载状态覆盖层 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>
    
    <h2 class="page-title">Banner管理</h2>
    <div class="content-container">
      <!-- 操作按钮区 -->
      <div class="action-buttons">
        <button @click="addNewBanner" class="btn-add">+ 添加新Banner</button>
      </div>

      <!-- Banner列表 -->
      <div class="banner-list">
        <div v-for="(banner, index) in banners" :key="banner.id || index" class="banner-item">
          <div class="banner-preview">
            <img :src="banner.src" :alt="banner.alt" class="preview-image" v-if="banner.src">
            <div class="no-image" v-else>无图片</div>
          </div>
          <div class="banner-form">
            <div class="form-group">
              <label>上传图片</label>
              <input 
                type="file" 
                accept="image/*" 
                @change="(e) => handleImageUpload(e, index)" 
                @click.stop 
                class="form-control"
              >
            </div>
            <div class="form-group">
              <label>图片描述</label>
              <input v-model="banner.alt" class="form-control" placeholder="请输入图片描述">
            </div>
            <div class="form-group">
              <label>排序号</label>
              <input v-model.number="banner.sortOrder" type="number" class="form-control" placeholder="数字越小越靠前">
            </div>
            <div class="form-group">
              <label>是否启用</label>
              <input type="checkbox" v-model="banner.isActive">
            </div>
            <div class="action-buttons-item">
              <button @click="saveBannerChanges(index)" class="btn-save">保存</button>
              <button @click="deleteBanner(index)" class="btn-delete">删除</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getBanners, createBanner, updateBanner, deleteBanner as deleteBannerApi } from '@/api/banner';
import { uploadImage } from '@/api/upload';

// 初始化banner数据
const banners = ref([]);
const router = useRouter();
const loading = ref(false);
const error = ref('');

// 从服务器加载banner数据
const loadBanners = async () => {
  loading.value = true;
  error.value = '';
  try {
    const data = await getBanners();
    banners.value = data;
  } catch (err) {
    console.error('加载Banner失败:', err);
    error.value = '加载Banner数据失败: ' + (err.message || '未知错误');
    alert(error.value);
  } finally {
    loading.value = false;
  }
};

// 处理图片上传
const handleImageUpload = async (e, index) => {
  // 阻止默认行为
  e.preventDefault();
  e.stopPropagation();

  const file = e.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append('file', file);

  try {
    loading.value = true;
    const result = await uploadImage(formData);
    // 更新banner对象的图片信息 - 只使用src和storagePath
    banners.value[index].src = result.src;
    banners.value[index].storagePath = result.storagePath;
    alert('图片上传成功，请点击保存按钮保存更改');
  } catch (err) {
    console.error('上传图片失败:', err);
    alert('上传图片失败: ' + (err.message || '未知错误'));
  } finally {
    loading.value = false;
    // 清空input值，以便能重复选择同一文件
    e.target.value = '';
  }
};

// 保存单个banner修改
const saveBannerChanges = async (index) => {
  const banner = banners.value[index];

  // 验证必填字段
  if (!banner.src) {
    alert('请上传图片');
    return;
  }

  try {
    loading.value = true;
    if (banner.id) {
      // 更新现有banner
      const updatedBanner = await updateBanner(banner.id, {
        src: banner.src,
        alt: banner.alt,
        storagePath: banner.storagePath,
        sortOrder: banner.sortOrder || 0,
        isActive: banner.isActive !== undefined ? banner.isActive : true
      });
      banners.value[index] = updatedBanner;
    } else {
      // 创建新banner
      const newBanner = await createBanner({
        src: banner.src,
        alt: banner.alt,
        storagePath: banner.storagePath,
        sortOrder: banner.sortOrder || 0,
        isActive: banner.isActive !== undefined ? banner.isActive : true
      });
      banners.value[index] = newBanner;
    }
    alert('保存成功');
  } catch (err) {
    console.error('保存Banner失败:', err);
    alert('保存Banner失败: ' + (err.message || '未知错误'));
  } finally {
    loading.value = false;
  }
};

// 添加新banner
const addNewBanner = () => {
  banners.value.push({
    alt: '',
    sortOrder: 0,
    isActive: true
  });
};

// 删除banner
const deleteBanner = async (index) => {
  const banner = banners.value[index];

  // 确认删除
  if (!confirm('确定要删除这个Banner吗？')) {
    return;
  }

  // 至少保留一个banner
  if (banners.value.length <= 1) {
    alert('至少保留一个Banner');
    return;
  }

  try {
    loading.value = true;
    if (banner.id) {
      // 删除服务器上的banner
      await deleteBannerApi(banner.id);
    }
    // 从列表中移除
    banners.value.splice(index, 1);
    alert('删除成功');
  } catch (err) {
    console.error('删除Banner失败:', err);
    alert('删除Banner失败: ' + (err.message || '未知错误'));
  } finally {
    loading.value = false;
  }
};

// 页面加载时初始化
onMounted(() => {
  // 检查登录状态
  const userStr = localStorage.getItem('adminUser');
  if (!userStr) {
    router.push('/admin/login');
    return;
  }
  loadBanners();
});
</script>

<style scoped lang="scss">
.banner-settings {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.action-buttons {
  margin-bottom: 20px;
  text-align: right;
}

.btn-add {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.banner-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.banner-item {
  display: flex;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.banner-preview {
  width: 300px;
  max-height: 400px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.no-image {
  color: #999;
  font-size: 14px;
}

.preview-image {
  max-width: 100%;
  max-height: 360px;
  object-fit: contain;
}

.banner-form {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.action-buttons-item {
  display: flex;
  gap: 10px;
  margin-top: auto;
  justify-content: flex-end;
}

.btn-save {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-delete {
  background-color: #f56c6c;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

/* 加载状态样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>